<template>
  <div class="grid-container" id="budgetViewGrid">
    <div v-if="leafParentData.length === 0 && budgetDataStore.getIsBudgetDataLoaded" class="no-data-message">
      <p>No budget data available for the selected projects.</p>
      <p class="sub-message">Budget data will be available once draw details are processed for these projects.</p>
    </div>
    <ag-grid
      v-else
      ref="agGridRef"
      :row-height="25"
      :row-data="leafParentData"
      :columnDefs="columnDefs"
      :override-width="!props.isAutoWidth"
      :key="`${leafParentDataCtr};${escalationQuery}${viewModeDivisor}`"
      @setChartData="setChartData"
      :pinnedBottomRowData="pinnedBottomRowData"
      :groupDefaultExpanded="1"
      :disable-auto-chart-emit="true"
      :suppressAggFuncInHeader="true"
      :groupIncludeFooter="false"
      :groupIncludeTotalFooter="false"
      :rowGroupPanelShow="'never'"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth_store";
import { useErrorStore } from "@/stores/error_store";
import { useBudgetDataStore } from "@/stores/budget_data_store";
import { useCompSetStore } from "@/stores/comp_set_store";
import { useMathSummaryStore } from "@/stores/math_summary_store";
import { useFormStore } from "@/stores/form_store";
import { axiosQuery, currencyFormat } from "@/assets/js/utils";
import AgGrid from "../../components/AgGrid.vue";
import { cleanupHierarchyNames } from "@/data/data";
import { median } from "@/assets/js/utils";
import { VIEW_MODES_TGU } from "@/types/Constants";

const emit = defineEmits(['setChartData']);

const props = defineProps({
  isAutoWidth: {
    type: Boolean,
    default: false,
  },
  shouldCalculateGrandTotal: {
    type: Boolean,
    default: true,
  },
  aggregationMethod: {
    type: String,
    default: 'viewByAverage', // 'viewByAverage' or 'viewMedian'
  },
});

// API and stores setup
const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";
const authStore = useAuthStore();
const errorStore = useErrorStore();
const budgetDataStore = useBudgetDataStore();
const compSetStore = useCompSetStore();
const mathSummaryStore = useMathSummaryStore();
const formSetStore = useFormStore();

// Grid data
const leafParentData = ref([]);
const branchGridData = ref({});
const leafParentNodesToData = {};
const leafParentDataCtr = ref(0);
const pinnedBottomRowData = ref([]);

// Grid configuration
let viewModeDivisor = 1;
let selectedProjects = {};
let escalationQuery = "";
let priorQuery = null;
let authToken = reactive({});

// Column definitions for the budget grid
// Only include visible columns - hidden columns are removed entirely
const columnDefs = ref([
  {
    field: "description",
    headerName: "Description",
    width: 300,
    rowGroup: false,
  },
  {
    field: "category",
    headerName: "Category",
    width: 200,
    rowGroup: true,
    hide: true,
  },
  {
    field: "original_budget",
    headerName: "Original Budget",
    width: 150,
    type: "numericColumn",
    aggFunc: 'sum',
    valueFormatter: (params) => currencyFormat(params.value, true),
  },
  {
    field: "current_budget", 
    headerName: "Current Budget",
    width: 150,
    type: "numericColumn",
    aggFunc: 'sum',
    valueFormatter: (params) => currencyFormat(params.value, true),
  },
  {
    field: "variance",
    headerName: "Variance %",
    width: 110,
    type: "numericColumn",
    // Calculate variance % from aggregated original and current budgets
    valueGetter: (params) => {
      if (params.node && params.node.group) {
        // For group rows, calculate from aggregated values
        const original = params.node.aggData?.original_budget || 0;
        const current = params.node.aggData?.current_budget || 0;
        return original > 0 ? (current - original) / original : 0;
      }
      return params.data?.variance || 0;
    },
    valueFormatter: (params) => {
      if (params.value !== null && params.value !== undefined) {
        return (params.value * 100).toFixed(1) + "%";
      }
      return "";
    },
  },
  {
    field: "variance_amount",
    headerName: "Variance $",
    width: 150,
    type: "numericColumn",
    aggFunc: 'sum',
    valueFormatter: (params) => currencyFormat(params.value, true),
  },
]);

// Main data fetching function
function fetchGridData() {
  console.log('🔥 BudgetGrid: fetchGridData called');
  viewModeDivisor = mathSummaryStore.getViewModeDivisor || 1;
  selectedProjects = compSetStore.getSelectedProjects;
  // Fix: Check for null/undefined explicitly, not just truthy (0 is a valid value)
  const storeEscalation = compSetStore.getEscalation;
  escalationQuery = (storeEscalation !== null && storeEscalation !== undefined && storeEscalation !== '') 
    ? `&escalation=${storeEscalation}` 
    : "";
  console.log('🔥 BudgetGrid: escalationQuery built =', escalationQuery, 'from compSetStore.getEscalation =', storeEscalation);
  
  // 🔥 CRITICAL FIX: ALWAYS compute selected IDs from the ACTUAL selection state
  // Do NOT use cached budgetDataStore.currentProjectIds - it gets stale when projects are toggled!
  // The compSetStore.selectedProjects object has the TRUE current selection state
  const actualSelectedIds = Object.keys(selectedProjects)
    .filter((id) => selectedProjects[id])
    .join(",");
  
  // Only fall back to budgetDataStore if compSetStore has NO selections at all
  // This handles the initial page load case where budgetDataStore was populated by a search
  const storeProjectIds = budgetDataStore.getCurrentProjectIds;
  const storeEscalationQuery = budgetDataStore.getCurrentEscalationQuery;
  
  // Use actual selection if we have any, otherwise fall back to store
  const selectedIds = actualSelectedIds || storeProjectIds;
  
  console.log('🔥 BudgetGrid: actualSelectedIds =', actualSelectedIds, 'storeProjectIds =', storeProjectIds, 'USING =', selectedIds);
  
  const finalEscalationQuery = storeEscalationQuery || escalationQuery;
  console.log('🔥🔥 BudgetGrid: finalEscalationQuery =', finalEscalationQuery);
  const currentQuery = `${selectedIds};${finalEscalationQuery};${viewModeDivisor}`;
  
  console.log('🔥 BudgetGrid: selectedIds =', selectedIds, 'currentQuery =', currentQuery, 'priorQuery =', priorQuery);
  
  if (selectedIds && priorQuery !== currentQuery) {
    priorQuery = currentQuery;
    
    // 🔥 USE NEW COMP SET SUMMARY ENDPOINT for multiple projects (pre-computed totals)
    // Include aggregation method (average or median)
    const aggregationParam = props.aggregationMethod === 'viewMedian' ? '&aggregation_method=median' : '&aggregation_method=average';
    const apiUrl = `${apiBase}projects/comp_set_summary?project_ids=${selectedIds}${finalEscalationQuery}${aggregationParam}`;
    console.log('🔥 BudgetGrid: Making API call to comp_set_summary:', apiUrl, 'aggregationMethod:', props.aggregationMethod);
    
    axiosQuery(
      apiUrl,
      authToken,
      (data) => {
        console.log('🔥 BudgetGrid: API response received:', data);
        processGridData(data);
        budgetDataStore.setIsBudgetDataLoaded(true);
      },
      errorStore,
      'Budget Grid'
    );
  } else {
    console.log('🔥 BudgetGrid: API call skipped - selectedIds:', selectedIds, 'query unchanged:', priorQuery === currentQuery);
  }
}

// Process the API data into grid format
function processGridData(data) {
  console.log('🔥 BudgetGrid: processGridData called with:', data);
  const dataArray = Array.isArray(data) ? data : [];
  
  if (dataArray.length === 0) {
    leafParentData.value = [];
    pinnedBottomRowData.value = [];
    budgetDataStore.setIsBudgetDataLoaded(true); // Still mark as loaded but with no data
    
    // CRITICAL FIX: Always emit setChartData even when there's no data
    console.log('🔥 BudgetGrid: No data found, emitting empty setChartData');
    emit('setChartData', []);
    return;
  }

  // 🔥 CRITICAL FIX: Check if this is comp set summary data (has 'key' and 'aggData' fields)
  const isCompSetSummary = dataArray.length > 0 && dataArray[0].key && dataArray[0].aggData;
  
  if (isCompSetSummary) {
    console.log('🔥 BudgetGrid: Detected comp set summary data - emitting raw data for charts');
    console.log('🔥 BudgetGrid: Sample row:', dataArray[0]);
    
    // Emit the raw data to CompSummary for charts
    emit('setChartData', dataArray);
    
    // Check if we're on the Budget Details page (where the grid is actually visible)
    // The route is /compsearch/budget, not just /budget
    const currentPath = window.location.pathname;
    const isOnBudgetPage = currentPath.includes('/compsearch/budget');
    
    console.log('🔥🔥 BudgetGrid: Page check - pathname:', currentPath, 'isOnBudgetPage:', isOnBudgetPage);
    
    if (!isOnBudgetPage) {
      console.log('🔥 BudgetGrid: On CompSummary page - skipping grid population to avoid double emit');
      leafParentData.value = [];
      pinnedBottomRowData.value = [];
      budgetDataStore.setIsBudgetDataLoaded(true);
      return;
    }
    
    console.log('🔥 BudgetGrid: On Budget page - building DDA hierarchical structure from API hierarchy_path');
    
    // Build grid using the hierarchy_path from the API (based on actual DDA term parent relationships)
    const leafParentDataTemp = [];
    
    // Helper to map category names to the 5 main categories
    const getMainCategory = (item) => {
      // Check category_override first
      const categoryOverride = (item.category_override || '').toUpperCase();
      if (categoryOverride) {
        if (categoryOverride.includes('ACQUISITION')) return 'Acquisition Costs';
        if (categoryOverride.includes('HARD')) return 'Hard Costs';
        if (categoryOverride.includes('SOFT')) return 'Soft Costs';
        if (categoryOverride.includes('FINANCING') || categoryOverride.includes('FINANCE')) return 'Financing Costs';
        if (categoryOverride.includes('CONTINGENC')) return 'Contingencies';
      }
      
      // Then check category_name
      const categoryName = (item.category_name || '').toUpperCase();
      if (categoryName.includes('ACQUISITION')) return 'Acquisition Costs';
      if (categoryName.includes('HARD')) return 'Hard Costs';
      if (categoryName.includes('SOFT')) return 'Soft Costs';
      if (categoryName.includes('FINANCING') || categoryName.includes('FINANCE')) return 'Financing Costs';
      if (categoryName.includes('CONTINGENC')) return 'Contingencies';
      
      // Fallback to key
      const key = (item.key || '').toUpperCase();
      if (key.includes('ACQUISITION')) return 'Acquisition Costs';
      if (key.includes('HARD')) return 'Hard Costs';
      if (key.includes('SOFT')) return 'Soft Costs';
      if (key.includes('FINANCING') || key.includes('FINANCE')) return 'Financing Costs';
      if (key.includes('CONTINGENC')) return 'Contingencies';
      
      return 'Other';
    };
    
    // Helper to create a grid row
    const createRow = (item) => {
      const aggData = item.aggData || {};
      const original = parseFloat(aggData.original_budget || 0);
      const current = parseFloat(aggData.current_budget || 0);
      const billed = parseFloat(aggData.total_billed || 0);
      const complete = parseFloat(aggData.percent_complete || 0);
      
      return {
        category: getMainCategory(item),
        scope_detail: item.dda_term_name || item.description,
        description: item.dda_term_name || item.description,
        original_budget: original / viewModeDivisor,
        current_budget: current / viewModeDivisor,
        variance: original > 0 ? (current - original) / original : 0,
        variance_amount: (current - original) / viewModeDivisor,
        total_spent: billed / viewModeDivisor,
        percent_complete: complete / 100,
        is_total: item.is_total || false,
        dda_term_id: item.dda_term_id,
      };
    };
    
    // Process all items - filter out total rows only
    dataArray.forEach(item => {
      // Skip total rows - check is_total flag and common total keywords in name
      const itemName = (item.dda_term_name || item.key || item.description || '').toLowerCase();
      const isTotal = item.is_total || 
                      itemName.includes('total') || 
                      itemName.includes('subtotal') ||
                      itemName.endsWith(' total');
      
      if (!isTotal) {
        leafParentDataTemp.push(createRow(item));
      }
    });
    
    // Sort by category first, then by description
    leafParentDataTemp.sort((a, b) => {
      const catCmp = (a.category || '').localeCompare(b.category || '');
      if (catCmp !== 0) return catCmp;
      return (a.description || '').localeCompare(b.description || '');
    });
    
    leafParentData.value = leafParentDataTemp;
    leafParentDataCtr.value++;
    budgetDataStore.setIsBudgetDataLoaded(true);
    
    console.log('🔥 BudgetGrid: DDA hierarchical grid populated with', leafParentDataTemp.length, 'rows using API hierarchy_path');
    return;
  }

  // Regular processing for single project detail data
  leafParentData.value = [];
  branchGridData.value = {};
  const leafParentDataTemp = [];

  dataArray.forEach((row) => {
    const description = row.scope_detail || row.description || 'Unknown Work Item';
    const contractorName = row.contractor_name || row.company || 'Unknown';
    
    // Parse numbers safely
    const originalBudget = parseFloat(row.original_budget || 0);
    const currentBudget = parseFloat(row.current_budget || 0);
    const previouslyFunded = parseFloat(row.previously_funded || 0);
    const currentRequest = parseFloat(row.current_request || 0);
    
    const aRow = {
      id: row.id,
      draw_number: row.draw_number || '',
      itemHierarchy: [`${description} (Draw ${row.draw_number || 'N/A'})`], // Make hierarchy unique with draw number
      scope_detail: description,
      description: description,
      contractor_name: contractorName,
      company: contractorName,
      original_budget: originalBudget / viewModeDivisor,
      current_budget: currentBudget / viewModeDivisor,
      variance: originalBudget > 0 ? (currentBudget - originalBudget) / originalBudget : 0,
      total_spent: (previouslyFunded + currentRequest) / viewModeDivisor,
      percent_complete: currentBudget > 0 ? (previouslyFunded + currentRequest) / currentBudget : 0,
    };

    leafParentDataTemp.push(aRow);
  });

  leafParentData.value = leafParentDataTemp;
  leafParentDataCtr.value++;
  
  // Calculate grand totals
  if (props.shouldCalculateGrandTotal) {
    calculateGrandTotal();
  }
  
  // Emit chart data ONLY for single project (NOT for comp sets)
  // Comp sets already emitted raw data earlier at line 203
  if (!isCompSetSummary) {
    emit('setChartData', leafParentDataTemp);
  }
}

// Calculate grand total row
function calculateGrandTotal() {
  const grandTotal = {
    itemHierarchy: ["Grand Total"],
    original_budget: 0,
    current_budget: 0,
    total_spent: 0,
    variance: 0,
    percent_complete: 0,
  };

  leafParentData.value.forEach(row => {
    grandTotal.original_budget += parseFloat(row.original_budget) || 0;
    grandTotal.current_budget += parseFloat(row.current_budget) || 0;
    grandTotal.total_spent += parseFloat(row.total_spent) || 0;
  });

  // Calculate overall variance
  if (grandTotal.original_budget > 0) {
    grandTotal.variance = (grandTotal.current_budget - grandTotal.original_budget) / grandTotal.original_budget;
  }

  // Calculate overall percent complete
  if (grandTotal.current_budget > 0) {
    grandTotal.percent_complete = grandTotal.total_spent / grandTotal.current_budget;
  }

  pinnedBottomRowData.value = [grandTotal];
}

// Chart data function for parent component
function setChartData(data) {
  emit('setChartData', data);
}

// Watch for changes in budget data store - watch the getFetchTimestamp getter to force refetch
watch(() => budgetDataStore.getFetchTimestamp, (newTimestamp) => {
  console.log('🔥 BudgetGrid: budgetDataStore.getFetchTimestamp changed to:', newTimestamp);
  const projectIds = budgetDataStore.getCurrentProjectIds;
  if (projectIds && newTimestamp > 0) {
    // Reset priorQuery to force refetch
    console.log('🔥 BudgetGrid: Resetting priorQuery to force refetch');
    priorQuery = null;
    fetchGridData();
  }
});

// Watch for changes in comp set store
watch(() => compSetStore.getSelectedProjects, (newSelectedProjects) => {
  if (Object.keys(newSelectedProjects).length > 0) {
    fetchGridData();
  }
}, { deep: true });

// 🔥 NEW: Watch for changes in aggregation method (Average vs Median)
watch(() => props.aggregationMethod, (newMethod, oldMethod) => {
  console.log('🔥 BudgetGrid: aggregationMethod changed from', oldMethod, 'to', newMethod);
  if (Object.keys(compSetStore.getSelectedProjects).length > 0 || budgetDataStore.getCurrentProjectIds) {
    fetchGridData();
  }
});

// Watch for view mode changes (GSF, Unit, Total)
watch(() => mathSummaryStore.getViewModeDivisor, (newDivisor) => {
  console.log('🔥 BudgetGrid: viewModeDivisor changed to:', newDivisor);
  fetchGridData();
});

// Watch for escalation changes using Pinia's $subscribe (more reliable than Vue watch)
let lastEscalation = compSetStore.escalation;
compSetStore.$subscribe((mutation, state) => {
  console.log('🔥🔥 BudgetGrid: PINIA $subscribe triggered - escalation:', state.escalation, 'lastEscalation:', lastEscalation);
  // Only refetch if escalation actually changed
  if (state.escalation !== lastEscalation) {
    console.log('🔥🔥🔥 BudgetGrid: Escalation CHANGED from', lastEscalation, 'to', state.escalation);
    lastEscalation = state.escalation;
    // Clear cached escalation query so fresh value is used
    budgetDataStore.currentEscalationQuery = '';
    // Reset priorQuery to force refetch with new escalation
    priorQuery = null;
    fetchGridData();
  }
});

// Initialize auth token and fetch data on mount
onMounted(() => {
  authToken = authStore.getAuthTokenHeader;
  
  // Initial fetch if we have data
  const hasStoreIds = budgetDataStore.getCurrentProjectIds;
  const hasSelectedProjects = Object.keys(compSetStore.getSelectedProjects).length > 0;
  
  if (hasStoreIds || hasSelectedProjects) {
    fetchGridData();
  }
});
</script>

<style scoped>
.grid-container {
  height: 500px;
  width: auto;
  max-width: 900px;
  margin: 0 auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #666;
  background: #f9f9f9;
  border-radius: 8px;
  padding: 40px;
}

.no-data-message p {
  margin: 12px 0;
  font-size: 16px;
  font-weight: 500;
}

.no-data-message p:first-child {
  font-size: 18px;
  color: #333;
}

.sub-message {
  font-size: 14px !important;
  color: #999 !important;
  font-weight: 400 !important;
}
</style>